import { useRef, useState, useEffect } from 'react'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'
import type { IconProp } from '@fortawesome/fontawesome-svg-core'

import { SidebarConfigProps } from '@/reducers/sidebar.slice'
import { User } from '@/reducers/auth/userReducer'
import { mtc, TextColors, TypographyStyles } from '@/styles'
import { Button, Icon, List, Popover, Text } from '@/ui'
import { NavList } from './NavList'
import { Header } from './Header'
import {
  rootStyles,
  footerStyles,
  footerMenuStyles,
  listStyles,
  listItemStyles,
  iconStyles,
} from './styles'

interface ExpandedProps {
  device: string
  footerOptions: Array<{ icon: IconProp; onClick: () => void; text: string }>
  categories?: Record<string, SidebarConfigProps> | null
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  user?: User | null
  selectedCategory?: string | null
}

export const Expanded = ({
  device,
  footerOptions,
  categories,
  isOpen,
  setIsOpen,
  user,
  selectedCategory,
}: ExpandedProps) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [showShadow, setShowShadow] = useState(false)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const el = scrollRef.current

    const handleScroll = () => {
      if (!el) return
      setShowShadow(el.scrollTop > 0)
    }

    if (el) {
      setShowShadow(el.scrollHeight > el.clientHeight)
      el.addEventListener('scroll', handleScroll)
      return () => el.removeEventListener('scroll', handleScroll)
    }

    // always return a function for consistent-return
    return () => {}
  }, [])

  return (
    <nav className={mtc(rootStyles())}>
      {/* scrollable content */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-6">
        <Header
          className=""
          device={device}
          handleIconClick={() => setIsOpen(false)}
          isOpen={isOpen}
        />

        <NavList
          categories={categories}
          className=""
          selectedCategory={selectedCategory}
        />
      </div>
      {/* Sticky footer */}
      <footer className={mtc(footerStyles({ showShadow }))}>
        <Popover open={open} onOpenChange={setOpen}>
          <Popover.Trigger asChild>
            <Button
              className={mtc(footerMenuStyles())}
              iconLeft={regular('user')}
              iconRight={regular('chevron-down')}
              iconRightClassName={mtc(
                'h-4 w-4 text-neutral-500 transition-transform duration-200',
                open && 'rotate-[-180deg]'
              )}
              variant="ghost"
            >
              <Text
                variant={TypographyStyles.H3}
                color={TextColors.EMPHASIS}
                className="flex-1 flex items-center gap-2"
              >
                {user?.firstName} {user?.lastName}
              </Text>
            </Button>
          </Popover.Trigger>

          <Popover.Content
            align="end"
            className="w-inherit rounded-2xl py-1.5 px-2"
          >
            <List className={mtc(listStyles())}>
              {footerOptions.map(({ icon, onClick, text }) => (
                <List.Item
                  onClick={onClick}
                  key={text}
                  className={mtc(listItemStyles())}
                >
                  <Icon icon={icon} className={mtc(iconStyles())} />

                  <Text
                    variant={TypographyStyles.BODY_SM}
                    className="flex-1 text-neutral-600"
                  >
                    {text}
                  </Text>
                </List.Item>
              ))}
            </List>
          </Popover.Content>
        </Popover>
      </footer>
    </nav>
  )
}
